import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import {
  HiXMark,
  HiDocumentText,
  HiUser,
  HiEye,
  HiEyeSlash,
  HiExclamationTriangle,
} from 'react-icons/hi2';

const ClientReportsViewer = ({ isOpen, onClose, client, userRole }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);

  const canViewReports = ['admin', 'moderator', 'coach'].includes(userRole);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const reportsRef = collection(firestore, 'clients', client.id, 'reports');
      const q = query(reportsRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);

      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp),
      }));

      setReports(reportsData);
    } catch (err) {
      setError('Failed to load reports. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [client?.id]);

  useEffect(() => {
    if (isOpen && canViewReports && client?.id) {
      fetchReports();
    }
  }, [isOpen, client?.id, canViewReports, fetchReports]);

  const formatDate = date => {
    if (!date) return 'Unknown date';
    try {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const handleClose = () => {
    setSelectedReport(null);
    setReports([]);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative max-h-[80vh] w-[92%] max-w-4xl rounded-xl border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5 sm:w-full"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4 md:px-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600">
                  <HiDocumentText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-black">Client Reports</h3>
                  <p className="text-sm text-gray-500">
                    {client.firstname || ''} {client.lastname || ''} - {reports.length} report
                    {reports.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="rounded-full p-2 text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600"
              >
                <HiXMark className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[calc(80vh-120px)] overflow-y-auto p-4 md:p-6">
              {!canViewReports ? (
                <div className="py-8 text-center">
                  <HiExclamationTriangle className="mx-auto h-12 w-12 text-red-500" />
                  <h3 className="mt-4 text-lg font-semibold text-black">Access Denied</h3>
                  <p className="mt-2 text-gray-600">
                    You do not have permission to view reports. Only administrators, business
                    advisors, and coaches can view reports.
                  </p>
                </div>
              ) : loading ? (
                <div className="py-8 text-center">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                  <p className="mt-2 text-gray-600">Loading reports...</p>
                </div>
              ) : error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-center gap-2">
                    <HiExclamationTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                </div>
              ) : reports.length === 0 ? (
                <div className="py-8 text-center">
                  <HiDocumentText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-semibold text-black">No Reports Found</h3>
                  <p className="mt-2 text-gray-600">
                    No reports have been submitted for this client yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reports.map(report => (
                    <motion.div
                      key={report.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                            <HiUser className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-black">
                              {report.submittedByName || report.submittedByEmail}
                            </p>
                            <p className="text-xs text-gray-500">
                              {report.submittedByRole} • {formatDate(report.timestamp)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            setSelectedReport(selectedReport?.id === report.id ? null : report)
                          }
                          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        >
                          {selectedReport?.id === report.id ? (
                            <HiEyeSlash className="h-4 w-4" />
                          ) : (
                            <HiEye className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      <div className="mb-3">
                        <p className="line-clamp-2 text-sm text-gray-700">{report.reportContent}</p>
                      </div>

                      <AnimatePresence>
                        {selectedReport?.id === report.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="border-t border-gray-100 pt-3">
                              <div className="rounded-lg bg-gray-50 p-3">
                                <p className="whitespace-pre-wrap text-sm text-gray-700">
                                  {report.reportContent}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ClientReportsViewer;
